// src/components/admin/kontroly/EmailNotifikaceTab.tsx
import React, { useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    FormControlLabel,
    Switch,
    Chip,
    Alert,
    CircularProgress,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { useSendTestEmailMutation } from '../../../api/JsdhNotifikaceService/service/EmailNastaveniMutations';

export interface EmailNotifikaceTabProps {
    emailEnabled: boolean;
    emailIntervaly: number[];
    emailPredmet: string;
    emailTelo: string;
    onEmailEnabledChange: (val: boolean) => void;
    onEmailIntervalyChange: (val: number[]) => void;
    onEmailPredmetChange: (val: string) => void;
    onEmailTeloChange: (val: string) => void;
}

const EmailNotifikaceTab: React.FC<EmailNotifikaceTabProps> = ({
    emailEnabled,
    emailIntervaly,
    emailPredmet,
    emailTelo,
    onEmailEnabledChange,
    onEmailIntervalyChange,
    onEmailPredmetChange,
    onEmailTeloChange,
}) => {
    const [newInterval, setNewInterval] = useState('');
    const [testEmailTo, setTestEmailTo] = useState('');
    const [previewOpen, setPreviewOpen] = useState(false);
    const sendTestMutation = useSendTestEmailMutation();

    const handleAddInterval = () => {
        const val = parseInt(newInterval, 10);
        if (!isNaN(val) && val >= 0 && !emailIntervaly.includes(val)) {
            onEmailIntervalyChange([...emailIntervaly, val]);
            setNewInterval('');
        }
    };

    const handleRemoveInterval = (den: number) => {
        onEmailIntervalyChange(emailIntervaly.filter((d) => d !== den));
    };

    const handleSendTest = () => {
        if (testEmailTo.trim()) {
            sendTestMutation.mutate({ to: testEmailTo.trim() });
        }
    };

    return (
        <Box sx={{ pt: 1 }}>
            <FormControlLabel
                control={
                    <Switch
                        checked={emailEnabled}
                        onChange={(e) => onEmailEnabledChange(e.target.checked)}
                    />
                }
                label="Odesílat e-mailové notifikace"
                sx={{ mb: 2 }}
            />

            {/* §7.1 upozornění — zaheslování PDF */}
            <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                    Zabezpečení přílohy (§ 7.1)
                </Typography>
                <Typography variant="body2">
                    PDF přílohy (lékařské posudky) jsou u členů s vyplněným datem narození automaticky
                    zaheslovány (heslo = datum narození ve formátu <strong>DDMMRRRR</strong>, příklad:
                    07041985 pro 7. dubna 1985). Členové bez vyplněného data narození dostanou přílohu
                    nezaheslovanou s prázdným datem — to doplní ručně na vytištěném formuláři
                    (doporučujeme datum doplnit do profilu).
                </Typography>
            </Alert>

            {/* Intervaly */}
            <Typography variant="subtitle2" gutterBottom>
                Intervaly upozornění (dní před expirací)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                {[...emailIntervaly].sort((a, b) => b - a).map((den) => (
                    <Chip
                        key={den}
                        label={den === 0 ? 'Den expirace' : `${den} dní`}
                        onDelete={() => handleRemoveInterval(den)}
                        size="small"
                        color="primary"
                        variant="outlined"
                    />
                ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                <TextField
                    label="Dní"
                    type="number"
                    size="small"
                    sx={{ width: 100 }}
                    inputProps={{ min: 0 }}
                    value={newInterval}
                    onChange={(e) => setNewInterval(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAddInterval();
                    }}
                    disabled={!emailEnabled}
                />
                <Button
                    size="small"
                    variant="outlined"
                    onClick={handleAddInterval}
                    disabled={!emailEnabled}
                >
                    Přidat
                </Button>
            </Box>

            {/* Předmět */}
            <TextField
                label="Předmět e-mailu"
                value={emailPredmet}
                onChange={(e) => onEmailPredmetChange(e.target.value)}
                fullWidth
                margin="dense"
                size="small"
                disabled={!emailEnabled}
                placeholder="Upomínka: platnost zdravotní prohlídky"
            />

            {/* Tělo */}
            <TextField
                label="Tělo e-mailu"
                value={emailTelo}
                onChange={(e) => onEmailTeloChange(e.target.value)}
                fullWidth
                margin="dense"
                size="small"
                multiline
                rows={6}
                disabled={!emailEnabled}
                placeholder={`Dobrý den,\n\nupozorňujeme Vás, že platnost Vaší zdravotní prohlídky brzy vyprší.\n\nV příloze naleznete lékařský posudek (heslo = datum narození DDMMRRRR).\n\nS pozdravem,\nSpráva JSDH`}
            />

            {/* Náhled */}
            {emailTelo && (
                <Box sx={{ mt: 1, mb: 2 }}>
                    <Button
                        size="small"
                        variant="text"
                        onClick={() => setPreviewOpen(!previewOpen)}
                        disabled={!emailEnabled}
                    >
                        {previewOpen ? 'Skrýt náhled' : 'Náhled těla e-mailu'}
                    </Button>
                    {previewOpen && (
                        <Box
                            sx={{
                                mt: 1,
                                p: 2,
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                bgcolor: 'background.default',
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'monospace',
                                fontSize: '0.8rem',
                            }}
                        >
                            {emailTelo}
                        </Box>
                    )}
                </Box>
            )}

            {/* Testovací e-mail */}
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Testovací e-mail
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', maxWidth: 480 }}>
                <TextField
                    label="Odeslat testovací e-mail na"
                    size="small"
                    fullWidth
                    value={testEmailTo}
                    onChange={(e) => setTestEmailTo(e.target.value)}
                    type="email"
                    disabled={!emailEnabled || sendTestMutation.isPending}
                    placeholder="vas@email.cz"
                />
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={
                        sendTestMutation.isPending ? <CircularProgress size={14} /> : <SendIcon />
                    }
                    onClick={handleSendTest}
                    disabled={!emailEnabled || !testEmailTo.trim() || sendTestMutation.isPending}
                    sx={{ whiteSpace: 'nowrap', mt: 0.5 }}
                >
                    Odeslat test
                </Button>
            </Box>
        </Box>
    );
};

export default EmailNotifikaceTab;
